import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "utils/api";
import { getProduct } from "api/store";
import SubHeader from "components/common/SubHeader";
import ShoppingCard from "components/card/ShoppingCard";
import ProductFilter from "components/filter/ProductFilter";
import homeIcon from "assets/images/icons/ic_home_line.svg";
import cartIcon from "assets/images/icons/cart_icon.svg";
import useRefreshCallback from "hooks/useRefreshCallback";
import Seo from "components/common/Seo";

const buildParams = (filters) => {
    const params = {};
    if (filters.categoryId) params.category = filters.categoryId;
    if (filters.subCategoryId) params.category = filters.subCategoryId;
    if (filters.brandIds?.length > 0) params.brand = filters.brandIds.join(",");
    if (filters.sort && filters.sort !== "recommended") params.sortType = filters.sort;
    if (filters.freeShipping) params.deliveryType = "free";
    if (filters.bundledShipping) params.deliveryType = "bundled";
    if (filters.priceMin != null) params.price_min = filters.priceMin;
    if (filters.priceMax != null) params.price_max = filters.priceMax;
    if (filters.minRating > 0) params.min_rating = filters.minRating;
    return params;
};

const ShoppingPage = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState({});
    const [isGridView, setIsGridView] = useState(true);

    const fetchProducts = useCallback((params) => {
        return getProduct(params).then((res) => {
            setProducts(res || []);
        });
    }, []);

    useRefreshCallback(() => fetchProducts(buildParams(filters)));

    useEffect(() => {
        Promise.all([
            getProduct(),
            api.get("/buccl_main/api/v1/categories/").then((res) => res.data).catch(() => []),
            api.get("/buccl_user/api/v1/brands/").then((res) => res.data).catch(() => []),
        ])
            .then(([productsRes, categoriesRes, brandsRes]) => {
                setProducts(productsRes || []);
                setCategories(
                    (categoriesRes || []).filter((c) => c.is_active && c.depth === 0)
                );
                setBrands((brandsRes || []).filter((b) => b.is_active));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const handleFilterChange = useCallback(
        (nextFilters) => {
            setFilters(nextFilters);
            const params = buildParams(nextFilters);
            fetchProducts(params);
        },
        [fetchProducts]
    );

    return (
        <div className="shopping-page">
            <Seo
                title="스토어"
                description="다이빙 장비와 관련 상품을 구매하세요. 프리다이빙·스쿠버다이빙 장비, 수영복 등 다양한 상품을 제공합니다."
            />
            <SubHeader
                title="스토어"
                menus={[
                  {
                    name: "홈",
                    icon: homeIcon,
                    callback: () => {
                        if (window.location.pathname !== "/") navigate("/");
                    },
                },
                    {
                        name: "장바구니",
                        icon: cartIcon,
                        callback: () => navigate("/cart"),
                    },
                ]}
            />

            <ProductFilter
                categories={categories}
                brands={brands}
                totalCount={products.filter(p => p.is_active).length}
                onFilterChange={handleFilterChange}
                onGridViewChange={setIsGridView}
            />

            <div
                className={`shopping-card-list${isGridView ? "" : " shopping-card-list--list"}`}
            >
                {products.filter(p => p.is_active).map((product) => (
                    <ShoppingCard key={product.id} info={product} />
                ))}
            </div>
        </div>
    );
};

export default ShoppingPage;
